import React, { useMemo, useState } from "react";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import EffectsPresetCard from "./effects-preset-card";
import EffectsPresetsCategoryTabs, { EffectsCategoryTabOption } from "./effects-presets-category-tabs";
import {
  EFFECT_PRESET_CATEGORY_LABELS,
  EffectPresetCategoryLabel,
  selectAvailableEffectPresets
} from "./effects-presets-data";
import type { EffectPreset, EffectPresetCategory } from "./effects-presets-types";

type CategoryFilter = EffectPresetCategory | "all";

export interface EffectsPresetsBrowserProps {
  onApply: (preset: EffectPreset) => void;
  /**
   * Whether a `hover` state renders on this screen's target. False on a mobile screen, where the panel
   * projects no hover at all — see {@link selectAvailableEffectPresets}. The catalogue, the counts and the
   * category tabs are all derived from the surviving presets, so a category left empty by the filter drops
   * out of the tab strip instead of sitting there reading 0.
   */
  hoverApplies: boolean;
}

const countByCategory = (presets: readonly EffectPreset[]): Record<string, number> =>
  presets.reduce((acc: Record<string, number>, preset: EffectPreset): Record<string, number> => {
    acc[preset.category] = (acc[preset.category] ?? 0) + 1;
    return acc;
  }, {});

const buildCategoryTabs = (presets: readonly EffectPreset[]): EffectsCategoryTabOption[] => {
  const counts: Record<string, number> = countByCategory(presets);
  return [
    { value: "all", label: "All", count: presets.length },
    ...EFFECT_PRESET_CATEGORY_LABELS.filter((c: EffectPresetCategoryLabel): boolean => (counts[c.value] ?? 0) > 0).map(
      (c: EffectPresetCategoryLabel): EffectsCategoryTabOption => ({
        value: c.value,
        label: c.label,
        count: counts[c.value] ?? 0
      })
    )
  ];
};

const matchesSearch = (preset: EffectPreset, query: string): boolean => {
  if (!query) return true;
  const lower: string = query.toLowerCase();
  if (preset.name.toLowerCase().includes(lower)) return true;
  if (preset.description.toLowerCase().includes(lower)) return true;
  if (preset.tags.some((t: string): boolean => t.toLowerCase().includes(lower))) return true;
  return false;
};

const EffectsPresetsBrowser: React.FC<EffectsPresetsBrowserProps> = ({ onApply, hoverApplies }) => {
  const [query, setQuery] = useState<string>("");
  const [category, setCategory] = useState<CategoryFilter>("all");

  const available: EffectPreset[] = useMemo(
    (): EffectPreset[] => selectAvailableEffectPresets(hoverApplies),
    [hoverApplies]
  );
  const categoryTabs: EffectsCategoryTabOption[] = useMemo(
    (): EffectsCategoryTabOption[] => buildCategoryTabs(available),
    [available]
  );

  const filteredPresets: EffectPreset[] = useMemo((): EffectPreset[] => {
    return available.filter((p: EffectPreset): boolean => {
      if (category !== "all" && p.category !== category) return false;
      if (!matchesSearch(p, query)) return false;
      return true;
    });
  }, [available, query, category]);

  return (
    <div className="effects-presets-popover">
      <div className="effects-presets-popover__search">
        <KuikaInput
          size="sm"
          value={query}
          placeholder="Search presets..."
          onChange={(_e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, next: string | number): void => {
            setQuery(typeof next === "string" ? next : String(next));
          }}
          leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
        />
      </div>
      <EffectsPresetsCategoryTabs
        options={categoryTabs}
        value={category}
        onChange={(next: string): void => setCategory(next as CategoryFilter)}
      />
      {filteredPresets.length === 0 ? (
        <div className="effects-presets-popover__empty">
          <KuikaSmallText style={{ color: "var(--dark-7)" }}>No presets match your search.</KuikaSmallText>
        </div>
      ) : (
        <div className="effects-presets-popover__grid">
          {filteredPresets.map((preset: EffectPreset, index: number): React.ReactNode => (
            <EffectsPresetCard
              key={preset.id}
              preset={preset}
              onApply={onApply}
              dataTestId={`effects-preset-card-${preset.id}`}
              enterIndex={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EffectsPresetsBrowser;
